import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePost } from './../../../apis/ApiRequests';
import { useNavigate } from 'react-router-dom';
import HeaderMobile from './components/Header';
import { addDays, format } from 'date-fns';
import { dateToString } from '../../../utils/Library';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BackspaceIcon from '@mui/icons-material/Backspace';
import InfoIcon from '@mui/icons-material/Info';
import { Button } from "@mui/material";
import DateOutlinedInput from '../../../components/input/DateOutlinedInput';
import CustomCheckbox from '../../../components/input/CustomCheckbox';
import CustomSnackBar from '../../../components/alert/CustomSnackBar';
import CustomSelect from '../../../components/select/CustomSelect';

const OUT_TIME ="時間外"

const PortRegisterMobile = () => {

    const { authInfo } = useAuth();
    const navigate = useNavigate();
    
    const today = format(addDays(new Date(), 0), 'yyyy-MM-dd');

    //-----------------------------------------------
    // initialize - セッション情報を取得
    //-----------------------------------------------
    const facilityCode = authInfo.facilityCode;
    const facilityName = authInfo.facilityName;
    const userId       = authInfo.userId;
    const userName     = authInfo.userName;
    const authority    = authInfo.authority;
    const traderCode   = authInfo.traderCode;
    const trader       = authInfo.trader;
    const senmeiCode   =
        authInfo.senmeiCode === null
            ? null
            : !isNaN(Number(authInfo.senmeiCode))
                ? Number(authInfo.senmeiCode)
                : authInfo.senmeiCode;
    const senmei       = authInfo.senmei;
    
    //-----------------------------------------------
    // initialize - useState
    //-----------------------------------------------
    const [ targetDate, setTargetDate] = useState(today);
    const [ dataGridHeight, setDataGridHeight ] = useState(window.innerHeight - 425);
    const [checkedCodes, setCheckedCodes] = useState([]);
    const [alert, setAlert] = useState(false);
    const [saveState, setSaveState] = useState(false);
    const [alertMessage, setAlertMessage] = useState();
    const [editingId, setEditingId] = useState(0);
    const [keepEditingId, setKeepEditingId] = useState(false);

    const [sortedSchedule, setSortedSchedule] = useState([])

    // 入船回数の最大
    const maxKaisu = useMemo(() => {
        if (!Array.isArray(sortedSchedule) || sortedSchedule.length === 0) {
            return 0;
        }
        return Math.max(...sortedSchedule.map(item => item.kaisu ?? 0));
    }, [sortedSchedule]);

    //===============================================
    // API
    //===============================================
	//-----------------------------------------------
    // API - searchData / masta_FishMethod
	//-----------------------------------------------
    const { response : searchDataFishMethod_res, post:searchDataFishMethod_Post } = usePost();
    const searchDataFishMethod = async () => {
        const postData = {
            'className'    : 'mch.models.masta.FishMethod',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
        }
        await searchDataFishMethod_Post(postData);
    }
    //-----------------------------------------------
    // API - searchDataSenmeiCodeAndDate / table_portschedule
    //-----------------------------------------------
    const { response : searchDataSenmeiCodeAndDate_res, post:searchDataSenmeiCodeAndDate_post } = usePost();
    const searchDataSenmeiCodeAndDate = async () => {
        if(!targetDate){
            return
        }
        const numericDate = dateToString(targetDate);
        const postData = {
            'className'    : 'mch.models.table.PortSchedule',
            'methodName'   : 'searchDataSenmeiCodeAndDate',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'senmeiCode'   : senmeiCode,
            "scheduleDate" : numericDate,
        }
        await searchDataSenmeiCodeAndDate_post(postData);
    };
    //-----------------------------------------------
    // API - getNextId / PortSchedule
    //-----------------------------------------------
    const { response : getNextId_Res, post : getNextIdPost } = usePost();
    const getNextId_func = async () => {
        const postData = {
            'className'    : 'mch.models.table.PortSchedule',
            'methodName'   : 'getNextId',
            'facilityCode' : facilityCode,
        }
        await getNextIdPost(postData);
    }
    //-----------------------------------------------
    // API - setMethodData / table_portschedule
    //-----------------------------------------------
    const { response : setMethodData_res, post:setMethodData_post } = usePost();

    const setMethodData = async (scheduleId) => {
        if(!targetDate){
            return
        }
        const target = Array.isArray(sortedSchedule)
            ? sortedSchedule.find(item => item.scheduleId === scheduleId)
            : null;
        const kaisu = target?.kaisu || maxKaisu + 1
        const numericDate = dateToString(targetDate);

        const methodCodeList = checkedCodes.length > 0 
            ? checkedCodes 
            : "";

        const methodList = Array.isArray(searchDataFishMethod_res?.Data) 
            ? searchDataFishMethod_res.Data
                .filter(item => checkedCodes.includes(item.code))
                .map(item => item.displayName)
            : "";
        const methodListOrNull = methodList.length > 0 ? methodList : "";
        
        const postData = {
            'className'      : 'mch.models.table.PortSchedule',
            'methodName'     : 'setMethodData',
            'facilityCode'   : facilityCode,
            'userId'         : userId,
            "scheduleDate"   : numericDate,
            "methodCodeList" : methodCodeList,
            "methodList"     : methodListOrNull,
			"activeFlag"     : 1,
            'senmeiCode'     : senmeiCode,
            'scheduleId'     : scheduleId,
            'kaisu'          : kaisu
        }
        return await setMethodData_post(postData);
    };
    //===============================================
    // useEffect
    //===============================================
    //-----------------------------------------------
    // useEffect - ロード時の設定
    //-----------------------------------------------
    useEffect(() => {

        searchDataFishMethod();
        searchDataSenmeiCodeAndDate();
        const handleResize = () => {
            setDataGridHeight(window.innerHeight - 425);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    },[]);  
    //-----------------------------------------------
    // useEffect - APIレスポンス(searchDataSenmeiCodeAndDate / table_portschedule)
	//-----------------------------------------------
    useEffect(() => {
        const sortSchedule = [...(searchDataSenmeiCodeAndDate_res?.Data ?? [])].sort(
            (a, b) => (a.kaisu ?? 0) - (b.kaisu ?? 0)
        )
        setSortedSchedule(sortSchedule)
        if(keepEditingId){
            setKeepEditingId(false);
            return;
        }
        setCheckedCodes([])

        if (sortSchedule[0]?.scheduleId) {
            setEditingId(sortSchedule[0]?.scheduleId);
            setCheckedCodes(sortSchedule[0]?.methodCodeList);
            return;
        }
        setEditingId("new");
    }, [searchDataSenmeiCodeAndDate_res]);
    //-----------------------------------------------
    // useEffect - 日付の変更
    //-----------------------------------------------
    useEffect(() =>{
        if(!targetDate){
            return;
        }
        searchDataSenmeiCodeAndDate();
    },[targetDate])
    //-----------------------------------------------
    // useEffect - APIレスポンス(setMethodData / table_portschedule)
	//-----------------------------------------------
    useEffect(() => {
        if(setMethodData_res?.statusCode === 1 &&setMethodData_res?.result === 1){
            //正常
            setSaveState(true);
            setKeepEditingId(true);
            searchDataSenmeiCodeAndDate();
        }else if (setMethodData_res?.statusCode === 0 &&setMethodData_res?.result !== 1) {
            //失敗
            setAlertMessage(setMethodData_res.result);
            setAlert(true);
        }
    }, [setMethodData_res]);
    //-----------------------------------------------
    // useEffect - APIレスポンス(getNextId / table_PortSchedule)
    //-----------------------------------------------
    useEffect(() => {
        if(getNextId_Res){
            setEditingId(getNextId_Res.scheduleId)
            setMethodData(getNextId_Res.scheduleId);
        }
    },[getNextId_Res])

    //===============================================
    // eventHandler
    //===============================================
    const handleClick = (menu) => {
        navigate(menu);
    }

    const header_date = {
        marginLeft   : '0px',
        marginRight  : '20px',
        padding      : '7px 10px 5px 10px',
        height       : '47px',
        borderRadius : '10px',
        color        : '#305496',
        fontSize     : '22px',
        width        : '100%',
        fontWeight   : 'bold'
    };
    
    //-----------------------------------------------
    // eventHandler - 保存実行
    //-----------------------------------------------
    const handleClick_Register_before = () => {
        //必須項目確認
        if (targetDate && facilityCode && senmeiCode !== null && userId) {
        if(editingId!=="new"){
            setMethodData(editingId);
            return;
        }
        getNextId_func()
        } else {
            const alertStr = []
            if(!userId){alertStr.push("「ユーザーID」")}
            if(!targetDate){alertStr.push("「日付」")}
            if(!facilityCode){alertStr.push("「施設コード」")}
            if(!senmeiCode){alertStr.push("「荷主コード」")}

            setAlertMessage(alertStr.join("、")+"がありません")
            setAlert(true)
        }
    };
    
    //-----------------------------------------------
    // eventHandler - 取消
    //-----------------------------------------------
    const handleClick_Cancel_before = () => {
        setCheckedCodes([]);
    };
    
    //-----------------------------------------------
    // eventHandler - 日付の変更
    //-----------------------------------------------
    const handleChange_entryDate = (value) => {
		setTargetDate(value);
        if(!value){
            setCheckedCodes([]);
            setEditingId("new");
        }
	};
    //-----------------------------------------------
    // eventHandler - 編集データの変更
    //-----------------------------------------------
    const handleChangeEditingId = (value) =>{
        setEditingId(value);
        
        const target = Array.isArray(sortedSchedule)
            ? sortedSchedule.find(item => item.scheduleId === value)
            : null;

        setCheckedCodes(target?.methodCodeList ?? []);
    }

    //-----------------------------------------------
    // eventHandler - チェックの変更
    //-----------------------------------------------
    const handleChange_searchStatus = (checked, code) => {
        setCheckedCodes(prev => {
            const current = Array.isArray(prev) ? prev : [];

            if (checked) {
                return current.includes(code) ? current : [...current, code];
            } else {
                return current.filter(c => c !== code);
            }
        });
    };

    const registerDataForSelect = Array.isArray(sortedSchedule) && targetDate
        ? [
            ...sortedSchedule.map((item) => {
                let name = `${item.kaisu}回目`;
                if(item.startMin === OUT_TIME || item.endMin === OUT_TIME){
                    name = `${name} ${OUT_TIME}`;
                } else if (item.startMin !== "" && item.startMin != null && item.endMin !== "" && item.endMin != null) {
                    name = `${name} - ${item.startMin.slice(0, 2)}:${item.startMin.slice(2)} ~ ${item.endMin.slice(0, 2)}:${item.endMin.slice(2)}`;
                }
                return {
                    id: item.scheduleId,
                    name: name,
                }
            }),{
                id: "new",
                name: `${maxKaisu + 1}回目`,
            },]
        : [{
            id: "new",
            name: "1回目",
        },];
    return (
        <>
            <HeaderMobile />
            <div className='portRegisterMobile bodyContens' style={{ marginTop: '60px' }}>
                <div style={header_date}>出漁登録</div>
                <div>
                    <DateOutlinedInput 
                        id       = "unloadList_targetDate"
                        value    = { targetDate }
                        width    = '100%'
                        onChange = { (e) => handleChange_entryDate(e.target.value) } 
                    />
                </div>
                <div style={{marginTop:'0.5em'}}>
                    <CustomSelect
                        id         = "select_editingId"
                        label      = "回数"
                        width      = '100%'
                        dataList   = {registerDataForSelect} 
                        selectedId = {editingId} 
                        onChange   = {(e) => handleChangeEditingId(e.target.value)} 
                    />
                </div>
                <div style={{marginTop:'1em', textAlign:'left',backgroundColor:'#fff',border : '1px solid #ababab',borderRadius : '10px', padding : '10px', width: '100%', height: dataGridHeight, marginBottom: '10px', overflowY:'scroll'}}>
                    {
                        (Array.isArray(searchDataFishMethod_res?.Data)
                            ? searchDataFishMethod_res.Data
                            : []
                        ).map(item => {
                            const checked = Array.isArray(checkedCodes) && checkedCodes.includes(item.code);

                            //CustomCheckbox の defaultChecked は内部的に Checkbox の checked に渡されている
                            return (
                                <CustomCheckbox
                                    key={item.code}
                                    label={item.displayName}
                                    defaultChecked={checked}
                                    onChange={(e) =>
                                        handleChange_searchStatus(e.target.checked, item.code)
                                    }
                                    style={{
                                        width: '260px',
                                        fontSize: '20px',
                                        backgroundColor: checked ? '#e0f2ff' : 'transparent',
                                        borderRadius: '8px',
                                        margin: '2px',
                                        '& .MuiFormControlLabel-label': { fontSize: '20px', textAlign: 'left', width: '260px' },
                                    }}
                                />
                            );
                        })
                    }
                </div>
                <div style={{ position: 'fixed', left: 0, bottom: 0, width: '100%', color: 'white', textAlign: 'center', }}>
                    <Button
                        color     = "primary"
                        size      = "large"
                        style     = {{ fontSize: '20px', width: '90%', marginTop: '1rem', marginBottom: '1rem', color:'#fff'}}
                        variant   = "contained"
                        startIcon = {<AddCircleIcon />}
                        onClick   = {() => handleClick_Register_before()}
                    >登録
                    </Button>
                    <Button
                        color     = "primary"
                        size      = "large"
                        style     = {{ fontSize: '20px', width: '90%', marginBottom: '1rem', color:'#fff', backgroundColor:'red' }}
                        variant   = "contained"
                        startIcon = {<BackspaceIcon />}
                        onClick   = {() => handleClick_Cancel_before()}
                    >取消
                    </Button>
                    <Button
                        color     = "secondary"
                        size      = "large"
                        style     = {{ fontSize: '20px', width: '90%',marginBottom: '1rem', color:'#fff', backgroundColor:'grey' }}
                        variant   = "contained"
                        startIcon = {<InfoIcon />}
                        onClick   = {() => { navigate(`/portHomeMobile`) }}
                    >ホームへ戻る
                    </Button>
                </div>
                <CustomSnackBar
                    open            = {saveState}
                    onClose         = {() => setSaveState(false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "登録しました"
                />
                <CustomSnackBar
                    open            = {alert}
                    onClose         = {() => setAlert(false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = {alertMessage}
                />
            </div>
        </>
    );
}

export default PortRegisterMobile;
